/**
 * Projects API Service
 *
 * Handles all project-related API calls.
 * Matches FastAPI backend snake_case conventions.
 */

import { api } from '../client';

export const projectsService = {
  /**
   * Get all projects
   * @returns {Promise<Array>} Array of all projects
   */
  async getAll() {
    const response = await api.post('/api/project', {});
    return response || [];
  },

  /**
   * Get projects for the current user.
   * Flow: GET /user/me → project_ids → POST /project with $in query
   * @returns {Promise<Array>} Array of user's projects
   */
  async getUserProjects() {
    const me = await api.get('/api/user/me');
    const projectIds = me.project_ids || [];

    if (projectIds.length === 0) {
      return [];
    }

    const response = await api.post('/api/project', {
      _id: { $in: projectIds },
    });
    return response || [];
  },

  /**
   * Get a single project by ID
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Project details
   */
  async getById(projectId) {
    const response = await api.post('/api/project', { _id: projectId });
    return response[0]; // API returns array
  },

  /**
   * Get multiple projects by IDs
   * @param {Array<string>} projectIds - Project IDs
   * @returns {Promise<Array>} Array of projects
   */
  async getByIds(projectIds) {
    if (!projectIds || projectIds.length === 0) {
      return [];
    }
    const response = await api.post('/api/project', {
      _id: { $in: projectIds },
    });
    return response || [];
  },

  /**
   * Create a new project
   * @param {Object} projectData - Project data (installation_ids, company_id, etc.)
   * @returns {Promise<Object>} Created project
   */
  async create(projectData) {
    return await api.put('/api/project', projectData);
  },

  /**
   * Update an existing project
   * @param {string} projectId - Project ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated project
   */
  async update(projectId, updates) {
    return await api.patch('/api/project', {
      query: { _id: projectId },
      update_data: updates,
    });
  },

  /**
   * Delete a project
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Deletion response
   */
  async delete(projectId) {
    return await api.delete('/api/project', {
      data: { _id: projectId },
    });
  },
};
